<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
import InstagramStory from 'dashboard/components-next/message/bubbles/InstagramStory.vue';
import BubbleContact from 'dashboard/components-next/message/bubbles/Contact.vue';
import BubbleFile from 'dashboard/components-next/message/bubbles/File.vue';

export default {
  name: 'MessagePreview',
  components: {
    AudioChip,
    InstagramStory,
    BubbleContact,
    BubbleFile,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
    short: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  data: () => {
    return {
      hasMediaLoadError: false,
    };
  },
  computed: {
    contentAttributes() {
      return this.message.content_attributes || {};
    },
    isAnInstagramStory() {
      return this.contentAttributes.image_type === 'story_mention';
    },
    attachments() {
      return this.message?.attachments || [];
    },
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      return this.getPlainText(subject || this.message.content);
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments;
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      return this.message && this.message.content_type === 'sticker';
    },
    hasLocationAttachment() {
      return this.attachments.some(
        attachment => attachment.file_type === 'location'
      );
    },
  },
  watch: {
    message() {
      this.hasMediaLoadError = false;
    },
  },
  mounted() {
    this.hasMediaLoadError = false;
  },
  methods: {
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video', 'story_mention', 'ig_reel'].includes(
        fileType
      );
    },
    normalizeAttachment(attachment) {
      return {
        ...attachment,
        dataUrl: attachment.data_url || attachment.dataUrl,
        fileType: attachment.file_type || attachment.fileType,
        transcribedText: attachment.transcribed_text || attachment.transcribedText,
      };
    },
    onMediaLoadError() {
      this.hasMediaLoadError = true;
    },
    locationMapUrl(attachment) {
      const lat = attachment.coordinates_lat ?? attachment.coordinatesLat;
      const lng = attachment.coordinates_long ?? attachment.coordinatesLong;

      if (lat == null || lng == null) return 'https://maps.google.com';

      return `https://maps.google.com/?q=${lat},${lng}`;
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="lock-closed"
      />
      <fluent-icon
        v-else-if="messageByAgent"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="arrow-reply"
      />
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="info"
      />
    </template>
    <span v-if="message.content && isMessageSticker">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        icon="image"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.image.CONTENT') }}
    </span>
    <span v-else-if="message.content && !hasLocationAttachment">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments">
      <div v-if="short">
        <fluent-icon
          v-if="attachmentIcon && showMessageType"
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-11"
          :icon="attachmentIcon"
        />
        {{ $t(`${attachmentMessageContent}`) }}
      </div>
      <div v-else>
        <div v-for="attachment in attachments" :key="attachment.id">
          <InstagramStory
            v-if="isAnInstagramStory"
            :story-url="attachment.thumb_url"
            @error="onMediaLoadError"
          />
          <AudioChip
            v-else-if="attachment.file_type === 'audio'"
            :attachment="normalizeAttachment(attachment)"
            @error="onMediaLoadError"
          />
          <img
            v-else-if="['image', 'story_mention', 'ig_reel'].includes(attachment.file_type)"
            :src="attachment.thumb_url || attachment.data_url"
            class="size-[72px] object-cover rounded-xl"
            @error="onMediaLoadError"
          >
          <video
            v-else-if="attachment.file_type === 'video'"
            :src="attachment.thumb_url || attachment.data_url"
            class="size-[72px] object-cover rounded-xl"
            muted
            playsinline
            @error="onMediaLoadError"
          />
          <a
            v-else-if="attachment.file_type === 'location'"
            :href="locationMapUrl(attachment)"
            rel="noreferrer noopener nofollow"
            target="_blank"
            class="inline-flex items-center gap-1 text-n-brand"
          >
            <fluent-icon
              size="16"
              class="-mt-0.5 align-middle inline-block"
              icon="location"
            />
            {{ attachment.fallback_title || $t('CHAT_LIST.ATTACHMENTS.location.CONTENT') }}
          </a>
          <BubbleContact
            v-else-if="attachment.file_type === 'contact'"
            :name="message.content"
            :phone-number="attachment.fallback_title"
          />
          <BubbleFile v-else :url="attachment.thumb_url" />
        </div>
      </div>
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
